/**
 * What two watering programs on one controller do to each other.
 *
 * Nothing in the stack refuses an overlapping pair — not the save routes, not
 * the dispatcher, not the bridge's scheduler — and the two transports fail at
 * it in different ways, neither of them loudly. On both, programs that take
 * turns on a valve are fine; it is running into each other that goes wrong:
 *
 *   ESP-NOW  A node holds exactly ONE schedule entry per relay. The server
 *            arms each relay with its next window and hands it the one after
 *            once that has run (espnowSchedulerService dayEntries /
 *            advanceControllerWindows), so two programs can share a valve at
 *            different times. Where their windows on a relay OVERLAP they are
 *            merged into one: a shared valve stays open from the first start
 *            to the last finish, whatever either program says — the clash.
 *            A shared master pump merged that way runs through both, which is
 *            what they need, so that is only the hydraulic warning.
 *
 *   XBee     The bridge runs each program as its own state machine and
 *            `_due_start` only refuses to re-enter the SAME program, so two
 *            programs run concurrently. `active_runs` is keyed on
 *            (router_mac, relay_index), so a shared valve's row belongs to
 *            whoever wrote it last; the first program to reach a sequence
 *            boundary then closes a valve the other is still watering through,
 *            and its end-of-cycle stops a pump the other still needs.
 *
 * A program can start several times a day (`starts`), each at its own length,
 * and every one of those runs is checked. A program running into its OWN next
 * start is findStartOverlaps in programStarts.ts, which the builder reports
 * separately — there is no other program to open.
 *
 * THE FRAME
 *   `start_time_sec` is seconds from midnight UTC and `days_of_week` is in
 *   LOCAL days (see CLAUDE.md). Comparing two programs on raw UTC seconds is
 *   the `localDaySec` bug in a different costume: on a +10 site an 08:00 local
 *   start is stored at 79200 and a 10:00 one at 0, so the morning program
 *   looks like it runs ten hours BEFORE the one two hours ahead of it. Both
 *   sides are folded into the local frame — the one `days_of_week` already
 *   selected for — and a window running past local midnight is split onto the
 *   following day, because that is the day it shares a valve with.
 */

import { localDaySec } from './irrigationRuns';
import { programRuns } from './programStarts';
import { tzOffsetSec } from './timezone';
import type { MasterPump, ScheduleProgram } from '../services/espnowIrrigation';

const DAY_SEC = 86400;

/** One relay on one node. A node alone is not an address — it drives several. */
export interface ValveAddress {
  router_mac: string;
  relay_index: number;
}

export type ConflictKind = 'shared-valve' | 'shared-pump' | 'time-overlap';

export interface ScheduleConflict {
  kind: ConflictKind;
  /**
   * `error` is a program that will not water what it says it waters.
   * `warning` is two programs that both water, at once — a hydraulic question
   * this cannot answer, since it does not know the line.
   */
  severity: 'error' | 'warning';
  /** The other program, as the one being edited sees it. */
  otherProgramId: string;
  otherProgramName: string;
  /** ISO weekdays (1 = Mon) on which both programs run. Never empty. */
  days: number[];
  /** The addresses both programs drive. Empty for `time-overlap`. */
  addresses: ValveAddress[];
  /** Seconds the two windows are open together, 0 when they never are. */
  overlapSec: number;
}

/** An address as a comparable key. MAC spelling varies by where it was typed. */
export function addressKey(routerMac: string, relayIndex: number): string {
  return `${String(routerMac ?? '').toUpperCase().replace(/[^0-9A-F]/g, '')}#${Number(relayIndex)}`;
}

/**
 * How long a program is open for, from its start to its last valve closing.
 *
 * Mirrors `programEntries()` in espnowSchedulerService.js: a step advances the
 * offset by `step_duration_sec`, but a relay carrying its own longer
 * `duration_sec` runs past its step, so the end of the program is the latest
 * relay close and not the sum of the steps. (The bridge truncates such a relay
 * at its step boundary instead, which makes this the longer of the two
 * readings — the right way to be wrong when the answer is a warning.)
 */
export function programDurationSec(program: ScheduleProgram): number {
  const steps = program.steps ?? [];
  let offset = 0;
  let end = 0;
  for (const step of steps) {
    const stepDuration = step.step_duration_sec || 0;
    for (const relay of step.relays ?? []) {
      end = Math.max(end, offset + (relay.duration_sec || stepDuration));
    }
    offset += stepDuration;
    end = Math.max(end, offset);
  }
  return Math.min(end, DAY_SEC);
}

/** One day's opening of a program, in seconds from LOCAL midnight. */
interface DayRun {
  day: number;
  start: number;
  end: number;
}

/**
 * Every (local day, window) a program occupies, one per start per day, each
 * at its own start's length.
 *
 * A window running past local midnight is split, with the tail landing on the
 * next day — the day it would actually collide on. Without the split a 23:30
 * program and a 00:30 one read as thirteen hours apart.
 */
function dayRuns(program: ScheduleProgram, offsetSec: number): DayRun[] {
  const runs: DayRun[] = [];
  for (const run of programRuns(program)) {
    const duration = programDurationSec(run);
    if (duration <= 0) continue;
    const start = localDaySec(run.start_time_sec || 0, offsetSec);
    for (const day of program.days_of_week ?? []) {
      if (start + duration <= DAY_SEC) {
        runs.push({ day, start, end: start + duration });
      } else {
        runs.push({ day, start, end: DAY_SEC });
        runs.push({ day: (day % 7) + 1, start: 0, end: start + duration - DAY_SEC });
      }
    }
  }
  return runs;
}

/** Seconds two programs are open together, and the days on which they are. */
function timeOverlap(a: DayRun[], b: DayRun[]): { overlapSec: number; days: number[] } {
  let overlapSec = 0;
  const days = new Set<number>();
  for (const runA of a) {
    for (const runB of b) {
      if (runA.day !== runB.day) continue;
      const shared = Math.min(runA.end, runB.end) - Math.max(runA.start, runB.start);
      if (shared > 0) {
        overlapSec = Math.max(overlapSec, shared);
        days.add(runA.day);
      }
    }
  }
  return { overlapSec, days: [...days].sort((x, y) => x - y) };
}

/**
 * Every address a program arms: its valves, plus one window per master pump it
 * draws on.
 *
 * Pump resolution copies `programEntries()` exactly, auto-assign included — a
 * relay naming no pump on a single-pump controller gets that pump. Guessing
 * differently here would report a pump clash the dispatcher does not create,
 * or miss the one it does.
 */
function programAddresses(
  program: ScheduleProgram,
  masterPumps: MasterPump[],
): { valves: Map<string, ValveAddress>; pumps: Map<string, ValveAddress> } {
  const valves = new Map<string, ValveAddress>();
  const pumpIds = new Set<string>();
  const singlePump = masterPumps.length === 1 ? masterPumps[0] : null;

  for (const step of program.steps ?? []) {
    for (const relay of step.relays ?? []) {
      if (!relay.router_mac) continue;
      valves.set(addressKey(relay.router_mac, relay.relay_index), {
        router_mac: relay.router_mac,
        relay_index: relay.relay_index,
      });
      const pumpId = relay.master_pump_id || (singlePump ? singlePump.id : null);
      if (pumpId) pumpIds.add(pumpId);
    }
  }

  const pumps = new Map<string, ValveAddress>();
  for (const pump of masterPumps) {
    if (!pumpIds.has(pump.id)) continue;
    pumps.set(addressKey(pump.router_mac, pump.relay_index), {
      router_mac: pump.router_mac,
      relay_index: pump.relay_index,
    });
  }
  return { valves, pumps };
}

function shared(a: Map<string, ValveAddress>, b: Map<string, ValveAddress>): ValveAddress[] {
  const out: ValveAddress[] = [];
  for (const [key, address] of a) {
    if (b.has(key)) out.push(address);
  }
  return out;
}

export interface ConflictOptions {
  /** `'xbee'` sites run their own timers; everything else is dispatched to. */
  transport?: string | null;
  masterPumps?: MasterPump[];
  orgTimezone?: string;
  /** Fixed clock, for tests and for keeping one render's DST offset stable. */
  now?: Date;
}

/**
 * Conflicts between every enabled pair of programs on one controller, keyed by
 * program id — each program's entry lists what the OTHERS do to it, so the
 * editor can show the selected program its own problems.
 *
 * Disabled programs are excluded entirely. A paused program dispatches nothing
 * and the bridge never starts it, so reporting it would be a warning nobody
 * can act on except by deleting a program they deliberately kept.
 */
export function findScheduleConflicts(
  programs: ScheduleProgram[],
  options: ConflictOptions = {},
): Map<string, ScheduleConflict[]> {
  const { transport = null, masterPumps = [], orgTimezone = 'UTC', now = new Date() } = options;
  const isXBee = transport === 'xbee';
  const offsetSec = tzOffsetSec(orgTimezone, now);

  const active = programs.filter(p => p.enabled);
  const prepared = active.map(program => ({
    program,
    runs: dayRuns(program, offsetSec),
    ...programAddresses(program, masterPumps),
  }));

  const byProgram = new Map<string, ScheduleConflict[]>();
  const push = (id: string, conflict: ScheduleConflict) => {
    const list = byProgram.get(id);
    if (list) list.push(conflict);
    else byProgram.set(id, [conflict]);
  };

  for (let i = 0; i < prepared.length; i++) {
    for (let j = i + 1; j < prepared.length; j++) {
      const a = prepared[i];
      const b = prepared[j];

      const { overlapSec, days: overlapDays } = timeOverlap(a.runs, b.runs);

      const sharedValves = shared(a.valves, b.valves);
      // An ESP-NOW pump shared by two overlapping programs has its windows
      // merged and runs through both, so it is not a clash there - only the
      // hydraulic question the time-overlap warning already asks.
      const sharedPumps = isXBee ? shared(a.pumps, b.pumps) : [];

      // A relay is only fought over while both programs are open. Taking
      // turns is fine on both transports.
      const addressDays = overlapDays;
      const addressesClash = overlapSec > 0;

      const report = (kind: ConflictKind, severity: 'error' | 'warning',
                      days: number[], addresses: ValveAddress[]) => {
        push(a.program.id, {
          kind, severity, days, addresses, overlapSec,
          otherProgramId: b.program.id, otherProgramName: b.program.name,
        });
        push(b.program.id, {
          kind, severity, days, addresses, overlapSec,
          otherProgramId: a.program.id, otherProgramName: a.program.name,
        });
      };

      if (addressesClash && sharedValves.length > 0) {
        report('shared-valve', 'error', addressDays, sharedValves);
      }
      if (addressesClash && sharedPumps.length > 0) {
        report('shared-pump', 'error', addressDays, sharedPumps);
      }
      // Reported on its own only where it is the whole of the story. Said
      // alongside a shared valve it adds nothing: the clash is already the
      // headline, and a second banner about the same pair reads as a second
      // fault.
      if (overlapSec > 0 && sharedValves.length === 0 && sharedPumps.length === 0) {
        report('time-overlap', 'warning', overlapDays, []);
      }
    }
  }

  return byProgram;
}

/** Does this program have anything that will stop it watering as written? */
export function hasBlockingConflict(conflicts: ScheduleConflict[] | undefined): boolean {
  return (conflicts ?? []).some(c => c.severity === 'error');
}
